import type { AvailableUnit } from '@/core/api/models/properties';
import { readListingUnitModel } from './pickMixedUnitImages';

export const BEDROOM_FILTER_OPTIONS = ['3BR', '4BR', '5BR', '6BR'] as const;

export type BedroomFilterOption = (typeof BEDROOM_FILTER_OPTIONS)[number];

export const DEFAULT_BEDROOM_FILTER: readonly BedroomFilterOption[] = ['3BR'];

/** Exclusive 3BR listing default (AED millions). */
export const LISTING_SLIDER_DEFAULT_M = 1.7;

/**
 * Default price-slider position for a bedroom selection.
 * Only exclusive 3BR lands at 1.7M; any other chip (or none) uses the 3M+ band.
 * The user can still drag the slider after this default is applied.
 */
export function defaultSliderMillionsForBedrooms(
  selected: readonly BedroomFilterOption[],
  maxSliderM: number,
): number {
  const onlyThreeBr = selected.length === 1 && selected[0] === '3BR';
  return onlyThreeBr ? LISTING_SLIDER_DEFAULT_M : maxSliderM;
}

/** Most suggestions listed under the unit number search. */
export const UNIT_SEARCH_MAX_RESULTS = 20;

export type FilterableUnit = Pick<AvailableUnit, 'unit_id' | 'unit_number' | 'number_of_bedrooms'> & {
  unit_model?: string;
};

const UNIT_MODEL_BHK_PATTERN = /^(\d+)\s*BHK$/i;

function isBedroomFilterOption(value: string): value is BedroomFilterOption {
  return (BEDROOM_FILTER_OPTIONS as readonly string[]).includes(value);
}

/**
 * `unit_model` "3BHK" → "3BR". Falls back to `number_of_bedrooms` ("3 Bed")
 * when the listing response has no usable `unit_model`.
 */
export function resolveUnitBedroomOption(unit: FilterableUnit): BedroomFilterOption | null {
  const bhkMatch = UNIT_MODEL_BHK_PATTERN.exec(readListingUnitModel(unit) ?? '');
  const count = bhkMatch?.[1] ?? String(Number.parseInt(unit.number_of_bedrooms ?? '', 10));
  const option = `${count}BR`;
  return isBedroomFilterOption(option) ? option : null;
}

/** An empty selection shows every unit. */
export function filterUnitsByBedrooms<T extends FilterableUnit>(
  units: readonly T[],
  selected: readonly BedroomFilterOption[],
): T[] {
  if (selected.length === 0) {
    return [...units];
  }
  const wanted = new Set<BedroomFilterOption>(selected);
  return units.filter((unit) => {
    const option = resolveUnitBedroomOption(unit);
    return option != null && wanted.has(option);
  });
}

function normalizeUnitNumber(value: string): string {
  return value.replaceAll(/\s+/g, '').toLowerCase();
}

/** Units whose number contains the query; spaces and case are ignored ("a1-40" matches "A1 -401"). */
export function searchUnitsByNumber<T extends FilterableUnit>(
  units: readonly T[],
  query: string,
  maxResults: number = UNIT_SEARCH_MAX_RESULTS,
): T[] {
  const needle = normalizeUnitNumber(query);
  if (!needle) {
    return [];
  }
  const matches: T[] = [];
  for (const unit of units) {
    if (normalizeUnitNumber(unit.unit_number ?? '').includes(needle)) {
      matches.push(unit);
      if (matches.length >= maxResults) {
        break;
      }
    }
  }
  return matches;
}

/** Order-independent equality for two bedroom selections. */
export function isSameBedroomSelection(
  a: readonly BedroomFilterOption[],
  b: readonly BedroomFilterOption[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every((option) => set.has(option));
}

/** Stable key for the applied selection (used to reset the viewport grid). */
export function bedroomSelectionKey(selected: readonly BedroomFilterOption[]): string {
  return BEDROOM_FILTER_OPTIONS.filter((option) => selected.includes(option)).join(',');
}
